import { type UnknownAction } from '@reduxjs/toolkit'

import {
  SEARCH_COLLECTIONS_REQUEST,
  SEARCHING_COLLECTIONS_SUCCEEDED,
  SEARCHING_COLLECTIONS_FAILED,
  INVALIDATE_COLLECTION_SEARCH_RESULTS,
  SEARCH_STREAMS_REQUEST,
  SEARCHING_STREAMS_SUCCEEDED,
  SEARCHING_STREAMS_FAILED,
  INVALIDATE_STREAM_SEARCH_RESULTS,
  SEARCH_ASSETS_REQUEST,
  SEARCHING_ASSETS_SUCCEEDED,
  SEARCHING_ASSETS_FAILED,
  INVALIDATE_ASSET_SEARCH_RESULTS,
  SEARCH_GROUPS_REQUEST,
  SEARCHING_GROUPS_FAILED,
  SEARCHING_GROUPS_SUCCEDDED,
  INVALIDATE_GROUPS_SEARCH_RESULTS,
  SEARCH_DEAL_ROOMS,
  SEARCHING_DEAL_ROOMS_SUCCEEDED,
  SEARCHING_DEAL_ROOMS_FAILED,
  INVALIDATE_DEAL_ROOMS_SEARCH_RESULTS,
  UPDATING_DEAL_ROOM_SUCCEEDED,
  SEARCH_LEARNERS_REQUEST,
  SEARCHING_LEARNERS_SUCCEEDED,
  SEARCHING_LEARNERS_FAILED,
  INVALIDATE_LEARNER_SEARCH_RESULTS,
  SEARCHING_CONTACT_FOR_AUTOCOMPLETE_SUCCEDDED,
  SEARCHING_CONTACT_FOR_AUTOCOMPLETE,
  SEARCHING_CONTACT_FOR_AUTOCOMPLETE_FAILED,
  INVALIDATE_AUTOCOMPLETE_CONTACT,
  UPDATING_COLLECTION_SUCCEEDED,
  DELETING_COLLECTION_SUCCEEDED,
  SEARCH_USERS_REQUEST,
  SEARCH_USERS_SUCCEDDED,
  SEARCH_USERS_FAILED,
  INVALIDATE_USERS_SEARCH_RESULTS,
  SEARCHING_USER_GROUPS_REQUEST,
  SEARCHING_USER_GROUPS_SUCCEDDED,
  SEARCHING_USER_GROUPS_FAILED,
  INVALIDATE_AUTOCOMPLETE_USER_GROUP,
  SEARCHING_CONTACT,
  SEARCHING_CONTACT_SUCCEDDED,
  SEARCHING_CONTACT_FAILED,
  INVALIDATE_CONTACT_SEARCH_RESULT,
  SEARCHING_COMPANIES,
  SEARCHING_COMPANIES_SUCCEDDED,
  SEARCHING_COMPANIES_FAILED,
  INVALIDATE_COMPANIES_SEARCH_RESULT,
  SEARCH_USER_ROLES_FOR_AUTO_COMPLETE,
  SEARCH_USER_ROLES_FOR_AUTO_COMPLETE_SUCCEEDED,
  SEARCH_USER_ROLES_FOR_AUTO_COMPLETE_FAILED
} from '../actions/actionTypes';

import _ from 'lodash';

import { SeachContactsRequest, SearchAssetsErrorResponse, SearchAssetsRequest, SearchAssetsResponse, SearchCollectionsErrorResponse, SearchCollectionsRequest, SearchCollectionsResponse, SearchCompaniesErrorResponse, SearchCompaniesRequest, SearchCompaniesResponse, SearchContactForAutoCompleteErrorResponse, SearchContactForAutoCompleteRequest, SearchContactForAutoCompleteResponse, SearchContactsErrorResponse, SearchContactsResponse, SearchDealRoomsErrorResponse, SearchDealRoomsRequest, SearchDealRoomsResponse, SearchGroupsErrorResponse, SearchGroupsRequest, SearchGroupsResponse, SearchLearnersErrorResponse, SearchLearnersRequest, SearchLearnersResponse, SearchStreamsErrorResponse, SearchStreamsRequest, SearchStreamsResponse, SearchUserGroupsErrorResponse, SearchUserGroupsRequest, SearchUserGroupsResponse, SearchUserRolesForAutoCompleteErrorResponse, SearchUserRolesForAutoCompleteRequest, SearchUserRolesForAutoCompleteResponse, SearchUsersErrorResponse, SearchUsersRequest, SearchUsersResponse } from '../../types/search';
import { Collection, DeleteCollectionResponse, UpdateCollectionResponse } from '../../types/collections'
import { Asset } from 'src/types/assets';
import { Stream } from 'src/types/streams';
import { Group } from 'src/types/groups';
import { DealRoom, UpdateDealRoomResponse } from '../../types/dealRoom';
import { Course } from 'src/types/coaching/courses';
import { Users } from 'src/types/streamAccessControl';
import { UserGroup } from 'src/types/users';
import { Contact } from 'src/types/contact';

interface EntityState<T> {
  query: string,
  loading: boolean,
  succeeded: boolean,
  items: T[],
  error: any,
  pagination: {
    currentPage: number,
    totalPages: number,
    recordsPerPage: number,
    totalRecordsCount: number,
    hasMoreItems: boolean
  },
}

interface SearchState {
  collections: EntityState<Collection>,
  streams: EntityState<Stream>,
  assets: EntityState<Asset>,
  groups: EntityState<Group>,
  dealRooms: EntityState<DealRoom>,
  courses: EntityState<Course>,
  learners: EntityState<any>,
  skills: EntityState<any>,
  contacts: EntityState<any>
  users: EntityState<Users>,
  userGroups: EntityState<UserGroup>,
  contact: EntityState<Contact>,
  company: EntityState<any>
  userRole: EntityState<any>
}

const initialEntityState: EntityState<any> = {
  query: '',
  loading: false,
  succeeded: false,
  items: [],
  error: null,
  pagination: {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 10,
    totalRecordsCount: 0,
    hasMoreItems: false
  }
}

const initialState: SearchState = {
  collections: initialEntityState,
  assets: initialEntityState,
  streams: initialEntityState,
  groups: initialEntityState,
  dealRooms: initialEntityState,
  courses: initialEntityState,
  learners: initialEntityState,
  skills: initialEntityState,
  contacts: initialEntityState,
  users: initialEntityState,
  userGroups: initialEntityState,
  userRole: initialEntityState,
  contact: initialEntityState,
  company: initialEntityState
}

export function searchReducer(state: SearchState = initialState, action: UnknownAction): SearchState {
  switch (action.type) {
    case SEARCH_COLLECTIONS_REQUEST: {
      const { query } = action.payload as SearchCollectionsRequest;

      return {
        ...state,
        collections: {
          ...state.collections,
          query: query,
          loading: true,
          error: null,
          succeeded: false,
        },
      };
    }

    case SEARCHING_COLLECTIONS_SUCCEEDED: {
      const { query, response } = action.payload as SearchCollectionsResponse;

      //Check if the result is for the same query
      if (state.collections.query != query) {
        return state;
      }

      const existingItems: Collection[] = state.collections.items || [];
      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");

      return {
        ...state,
        collections: {
          ...state.collections,
          query: query,
          loading: false,
          items: records,
          succeeded: true,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
        },
      };
    }

    case SEARCHING_COLLECTIONS_FAILED: {
      const { query, error } = action.payload as SearchCollectionsErrorResponse;

      //Check if the result is for the same query
      if (state.collections.query != query) {
        return state;
      }

      return {
        ...state,
        collections: {
          ...state.collections,
          query: query,
          loading: false,
          error: error,
          succeeded: false,
        },
      };
    }

    case INVALIDATE_COLLECTION_SEARCH_RESULTS: {
      return {
        ...state,
        collections: initialEntityState,
      };
    }

    case SEARCH_DEAL_ROOMS: {
      const { query } = action.payload as SearchDealRoomsRequest;

      return {
        ...state,
        dealRooms: {
          ...state.dealRooms,
          query: query,
          loading: true,
          error: null,
          succeeded: false,
        },
      };
    }

    case SEARCHING_DEAL_ROOMS_SUCCEEDED: {
      const { query, response } = action.payload as SearchDealRoomsResponse;

      //Check if the result is for the same query
      if (state.dealRooms.query != query) {
        return state;
      }

      const existingItems: DealRoom[] = state.dealRooms.items || [];
      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");

      return {
        ...state,
        dealRooms: {
          ...state.dealRooms,
          query: query,
          loading: false,
          items: records,
          succeeded: true,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
        },
      };
    }

    case SEARCHING_DEAL_ROOMS_FAILED: {
      const { query, error } = action.payload as SearchDealRoomsErrorResponse;

      //Check if the result is for the same query
      if (state.dealRooms.query != query) {
        return state;
      }

      return {
        ...state,
        dealRooms: {
          ...state.dealRooms,
          query: query,
          loading: false,
          error: error,
          succeeded: false,
        },
      };
    }

    case INVALIDATE_DEAL_ROOMS_SEARCH_RESULTS: {
      return {
        ...state,
        dealRooms: initialEntityState,
      };
    }

    //Streams
    case SEARCH_STREAMS_REQUEST: {
      const { query } = action.payload as SearchStreamsRequest;

      return {
        ...state,
        streams: {
          ...state.streams,
          query: query,
          loading: true,
          error: null,
          succeeded: false,
        },
      };
    }

    case SEARCHING_STREAMS_SUCCEEDED: {
      const { query, response } = action.payload as SearchStreamsResponse;

      //Check if the result is for the same query
      if (state.streams.query != query) {
        return state;
      }

      const existingItems: Stream[] = state.streams.items || [];
      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");

      return {
        ...state,
        streams: {
          ...state.streams,
          query: query,
          loading: false,
          items: records,
          succeeded: true,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
        },
      };
    }

    case SEARCHING_STREAMS_FAILED: {
      const { query, error } = action.payload as SearchStreamsErrorResponse;

      //Check if the result is for the same query
      if (state.streams.query != query) {
        return state;
      }

      return {
        ...state,
        streams: {
          ...state.streams,
          query: query,
          loading: false,
          error: error,
          succeeded: false,
        },
      };
    }

    case INVALIDATE_STREAM_SEARCH_RESULTS: {
      return {
        ...state,
        streams: initialEntityState,
      };
    }

    //Assets
    case SEARCH_ASSETS_REQUEST: {
      const { query } = action.payload as SearchAssetsRequest;

      return {
        ...state,
        assets: {
          ...state.assets,
          query: query,
          loading: true,
          error: null,
          succeeded: false,
        },
      };
    }

    case SEARCHING_ASSETS_SUCCEEDED: {
      const { query, response } = action.payload as SearchAssetsResponse;

      //Check if the result is for the same query
      if (state.assets.query != query) {
        return state;
      }

      const existingItems: Asset[] = state.assets.items || [];
      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");

      return {
        ...state,
        assets: {
          ...state.assets,
          query: query,
          loading: false,
          items: records,
          succeeded: true,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
        },
      };
    }

    case SEARCHING_ASSETS_FAILED: {
      const { query, error } = action.payload as SearchAssetsErrorResponse;

      //Check if the result is for the same query
      if (state.assets.query != query) {
        return state;
      }

      return {
        ...state,
        assets: {
          ...state.assets,
          query: query,
          loading: false,
          error: error,
          succeeded: false,
        },
      };
    }

    case INVALIDATE_ASSET_SEARCH_RESULTS: {
      return {
        ...state,
        assets: initialEntityState,
      };
    }

    case SEARCH_GROUPS_REQUEST: {
      const { query } = action.payload as SearchGroupsRequest;

      return {
        ...state,
        groups: {
          ...state.groups,
          query: query,
          loading: true,
          error: null,
          succeeded: false,
        },
      };
    }

    case SEARCHING_GROUPS_SUCCEDDED: {
      const { query, response } = action.payload as SearchGroupsResponse;

      //Check if the result is for the same query
      if (state.groups.query != query) {
        return state;
      }

      const existingItems = state.groups.items || [];
      let records = response.records;
      if (response && response.currentPage && response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");

      const currentPage = _.get(response, "currentPage", 0);
      const totalPages = _.get(response, "totalPages", 0);

      return {
        ...state,
        groups: {
          ...state.groups,
          query: query,
          loading: false,
          items: records,
          succeeded: true,
          pagination: {
            currentPage: currentPage,
            totalPages: totalPages,
            recordsPerPage: _.get(response, "recordsPerPage", 0),
            totalRecordsCount: _.get(response, "totalRecordsCount", 0),
            hasMoreItems: currentPage < totalPages - 1,
          },
        },
      };
    }

    case SEARCHING_GROUPS_FAILED: {
      const { query, error } = action.payload as SearchGroupsErrorResponse;

      //Check if the result is for the same query
      if (state.groups.query != query) {
        return state;
      }

      return {
        ...state,
        groups: {
          ...state.groups,
          query: query,
          loading: false,
          error: error,
          succeeded: false,
        },
      };
    }

    case INVALIDATE_GROUPS_SEARCH_RESULTS: {
      return {
        ...state,
        groups: initialEntityState,
      };
    }

    case UPDATING_DEAL_ROOM_SUCCEEDED: {
      const { response } = action.payload as UpdateDealRoomResponse;
      const id = response.id;

      const items = _.map(state.dealRooms.items, (item) => {
        if (item.id === id) {
          return response;
        }
        return item;
      });

      return {
        ...state,
        dealRooms: {
          ...state.dealRooms,
          items: items,
        },
      };
    }
    case SEARCHING_CONTACT_FOR_AUTOCOMPLETE:
      var { query } = action.payload as SearchContactForAutoCompleteRequest;

      var items = state.contacts.items;
      if (!query) {
        items = [];
      }
      return Object.assign({}, state, {
        ...state,
        contacts: {
          ...state.contacts,
          query: action.payload,
          loading: true,
          items: items,
        },
      });

    case SEARCHING_CONTACT_FOR_AUTOCOMPLETE_SUCCEDDED:
      var { query, response } =
        action.payload as SearchContactForAutoCompleteResponse;

      let contactItems = response.records;

      return Object.assign({}, state, {
        ...state,
        contacts: {
          ...state.contacts,
          query: query,
          loading: false,
          items: contactItems,
        },
      });

    case SEARCHING_CONTACT_FOR_AUTOCOMPLETE_FAILED:
      var { error } =
        action.payload as SearchContactForAutoCompleteErrorResponse;
      return Object.assign({}, state, {
        ...state,
        contacts: {
          ...state.contacts,
          query: action.payload,
          loading: false,
          items: [],
          error: error,
        },
      });

    case INVALIDATE_AUTOCOMPLETE_CONTACT:
      return Object.assign({}, state, {
        ...state,
        contacts: {
          ...state.contacts,
          loading: false,
          items: [],
          error: null,
        },
      });

    case SEARCH_LEARNERS_REQUEST: {
      const { query, page } = action.payload as SearchLearnersRequest;

      let existingItems: any[] = state.learners.items || [];
      if (page === 0) {
        existingItems = [];
      }

      return {
        ...state,
        learners: {
          ...state.learners,
          query: query || "",
          loading: true,
          error: null,
          succeeded: false,
          items: existingItems
        }
      }
    }

    case SEARCHING_LEARNERS_SUCCEEDED: {
      const { query, response } = action.payload as SearchLearnersResponse;

      //Check if the result is for the same query
      if (state.learners.query != query) {
        return state;
      }

      const existingItems: any[] = state.learners.items || [];
      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, 'id');

      return {
        ...state,
        learners: {
          ...state.learners,
          query: query,
          loading: false,
          items: records,
          succeeded: true,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1
          },
        }
      }
    }

    case SEARCHING_LEARNERS_FAILED: {
      const { query, error } = action.payload as SearchLearnersErrorResponse;

      //Check if the result is for the same query
      if (state.learners.query != query) {
        return state;
      }

      return {
        ...state,
        learners: {
          ...state.learners,
          query: query,
          loading: false,
          error: error,
          succeeded: false
        }
      }
    }

    case INVALIDATE_LEARNER_SEARCH_RESULTS: {
      return {
        ...state,
        learners: initialEntityState
      }
    }

    case UPDATING_COLLECTION_SUCCEEDED: {
      const { response } = action.payload as UpdateCollectionResponse;
      const id = response.id;
      if (!id) {
        return state;
      }

      const items = _.map(state.collections.items, (item) => {
        if (item.id === id) {
          return response;
        }
        return item;
      });
      return {
        ...state,
        collections: {
          ...state.collections,
          items: items,
        },
      };
    }

    case DELETING_COLLECTION_SUCCEEDED : {
      const { id } = action.payload as DeleteCollectionResponse;
      if (!id) {
        return state;
      }
      const items = _.filter(state.collections.items, (item) => {
        if (item.id === id) {
          return false;
        }
        return true;
      });
      return {
        ...state,
        collections: {
          ...state.collections,
          items: items,
        },
      };
    }

    case SEARCH_USERS_REQUEST: {
      const { query } = action.payload as SearchUsersRequest;

      return {
        ...state,
        users: {
          ...state.users,
          query: query,
          loading: true,
          error: null,
          succeeded: false,
        },
      };
    }

    case SEARCH_USERS_SUCCEDDED: {
      const { query, response } = action.payload as SearchUsersResponse;

      //Check if the result is for the same query
      if (state.users.query != query) {
        return state;
      }

      const existingItems: Users[] = state.users.items || [];
      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");

      return {
        ...state,
        users: {
          ...state.users,
          query: query,
          loading: false,
          items: records,
          succeeded: true,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
        },
      };
    }

    case SEARCH_USERS_FAILED: {
      const { query, error } = action.payload as SearchUsersErrorResponse;

      //Check if the result is for the same query
      if (state.users.query != query) {
        return state;
      }

      return {
        ...state,
        users: {
          ...state.users,
          query: query,
          loading: false,
          error: error,
          succeeded: false,
        },
      };
    }

    case INVALIDATE_USERS_SEARCH_RESULTS: {
      return {
        ...state,
        users: initialEntityState,
      };
    }

    case SEARCHING_USER_GROUPS_REQUEST: {
      const { query } = action.payload as SearchUserGroupsRequest;

      return {
        ...state,
        userGroups: {
          ...state.userGroups,
          query: query,
          loading: true,
          error: null,
          succeeded: false,
        },
      };
    }

    case SEARCHING_USER_GROUPS_SUCCEDDED: {
      const { query, response } = action.payload as SearchUserGroupsResponse;

      //Check if the result is for the same query
      if (state.userGroups.query != query) {
        return state;
      }

      const existingItems: UserGroup[] = state.userGroups.items || [];
      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");

      return {
        ...state,
        userGroups: {
          ...state.userGroups,
          query: query,
          loading: false,
          items: records,
          succeeded: true,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
        },
      };
    }

    case SEARCHING_USER_GROUPS_FAILED: {
      const { query, error } = action.payload as SearchUserGroupsErrorResponse;

      //Check if the result is for the same query
      if (state.userGroups.query != query) {
        return state;
      }

      return {
        ...state,
        userGroups: {
          ...state.userGroups,
          query: query,
          loading: false,
          error: error,
          succeeded: false,
        },
      };
    }

    case INVALIDATE_AUTOCOMPLETE_USER_GROUP: {
      return {
        ...state,
        userGroups: initialEntityState,
      };
    }

    case SEARCHING_CONTACT: {
      const { query } = action.payload as SeachContactsRequest;

      return {
        ...state,
        contact: {
          ...state.contact,
          query: query,
          loading: true,
          error: null,
          succeeded: false,
        },
      };
    }

    case SEARCHING_CONTACT_SUCCEDDED: {
      const { query, response } = action.payload as SearchContactsResponse;

      //Check if the result is for the same query
      if (state.contact.query != query) {
        return state;
      }

      const existingItems: Contact[] = state.contact.items || [];
      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");

      return {
        ...state,
        contact: {
          ...state.contact,
          query: query,
          loading: false,
          items: records,
          succeeded: true,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
        },
      };
    }

    case SEARCHING_CONTACT_FAILED: {
      const { query, error } = action.payload as SearchContactsErrorResponse;

      //Check if the result is for the same query
      if (state.contact.query != query) {
        return state;
      }

      return {
        ...state,
        contact: {
          ...state.contact,
          query: query,
          loading: false,
          error: error,
          succeeded: false,
        },
      };
    }

    case INVALIDATE_CONTACT_SEARCH_RESULT: {
      return {
        ...state,
        contact: initialEntityState,
      };
    }

    case SEARCHING_COMPANIES: {
      const { query } = action.payload as SearchCompaniesRequest;

      return {
        ...state,
        company: {
          ...state.company,
          query: query,
          loading: true,
          error: null,
          succeeded: false,
        },
      };
    }

    case SEARCHING_COMPANIES_SUCCEDDED: {
      const { query, response } = action.payload as SearchCompaniesResponse;

      //Check if the result is for the same query
      if (state.company.query != query) {
        return state;
      }

      const existingItems: any[] = state.company.items || [];
      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");

      return {
        ...state,
        company: {
          ...state.company,
          query: query,
          loading: false,
          items: records,
          succeeded: true,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
        },
      };
    }

    case SEARCHING_COMPANIES_FAILED: {
      const { query, error } = action.payload as SearchCompaniesErrorResponse;

      //Check if the result is for the same query
      if (state.company.query != query) {
        return state;
      }

      return {
        ...state,
        company: {
          ...state.company,
          query: query,
          loading: false,
          error: error,
          succeeded: false,
        },
      };
    }

    case INVALIDATE_COMPANIES_SEARCH_RESULT: {
      return {
        ...state,
        company: initialEntityState,
      };
    }

    case SEARCH_USER_ROLES_FOR_AUTO_COMPLETE: {
      const { query } = action.payload as SearchUserRolesForAutoCompleteRequest;
    
      return {
        ...state,
        userRole: {
          ...state.userRole,
          query: query,
          loading: true,
          error: null,
          succeeded: false,
        },
      };
    }
    
    case SEARCH_USER_ROLES_FOR_AUTO_COMPLETE_SUCCEEDED: {
      const { query, response } = action.payload as SearchUserRolesForAutoCompleteResponse;
    
      if (state.userRole.query !== query) {
        return state;
      }
      
      let records = response;
  
      records = _.uniqBy(records, "id");
    
      return {
        ...state,
        userRole: {
          ...state.userRole,
          query: query,
          loading: false,
          items: records,
          succeeded: true,
        },
      };
    }
    
    case SEARCH_USER_ROLES_FOR_AUTO_COMPLETE_FAILED: {
      const { query, error } = action.payload as SearchUserRolesForAutoCompleteErrorResponse;
    
      // Check if the result is for the same query
      if (state.userRole.query !== query) {
        return state;
      }
    
      return {
        ...state,
        userRole: {
          ...state.userRole,
          query: query,
          loading: false,
          error: error,
          succeeded: false,
        },
      };
    }
    
    default: {
      return state;
    }
  }
}